import { Safari16API } from '@chiroup/client-core/functions/Safari16API';
import { initSentryFrontend } from '@chiroup/core/config/sentryFrontend';
import { Amplify } from 'aws-amplify';
import { createRoot } from 'react-dom/client';
import App from './App';
import { kioskSession } from './services/kioskSession';
import { portalSession } from './services/portalSession';
import { requestSession } from './services/requestSession';
import './styles/index.css';

// Polyfill ResizeObserver for iOS 16 WebViews where it is unavailable.
// cmdk@1.1.1 calls `new ResizeObserver()` unconditionally on mount, which
// throws a ReferenceError in these environments.
if (typeof ResizeObserver === 'undefined') {
  (globalThis as Record<string, unknown>).ResizeObserver = class ResizeObserver {
    private callback: ResizeObserverCallback;
    private targets: Element[] = [];
    private frameId: number | null = null;

    constructor(callback: ResizeObserverCallback) {
      this.callback = callback;
    }

    observe(target: Element) {
      if (!this.targets.includes(target)) {
        this.targets.push(target);
      }
      this.schedule();
    }

    unobserve(target: Element) {
      this.targets = this.targets.filter(t => t !== target);
    }

    disconnect() {
      this.targets = [];
      if (this.frameId !== null) {
        cancelAnimationFrame(this.frameId);
        this.frameId = null;
      }
    }

    private schedule() {
      if (this.frameId !== null) return;
      const loop = () => {
        if (this.targets.length === 0) {
          this.frameId = null;
          return;
        }
        const entries: ResizeObserverEntry[] = this.targets.map(target => ({
          target,
          contentRect: target.getBoundingClientRect(),
          borderBoxSize: [],
          contentBoxSize: [],
          devicePixelContentBoxSize: [],
        }));
        this.callback(entries, this as unknown as ResizeObserver);
        this.frameId = requestAnimationFrame(loop);
      };
      this.frameId = requestAnimationFrame(loop);
    }
  };
}

// Initialize Sentry with shared configuration
initSentryFrontend({
  dsn: 'https://aa0d06c160045a358dae800d79a15999@o4506354335612928.ingest.us.sentry.io/4506356717977600',
  apiEndpoint: import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string,
  stage: import.meta.env.VITE_STAGE,
  version: import.meta.env.VITE_VERSION,
});

// Custom auth replaces Cognito. We still use Amplify's REST client
// (it's a thin wrapper around fetch and the rest of the codebase
// uses it), but the Authorization header now carries our own session
// token from `portalSession` instead of a Cognito ID token.
//
// `Auth` is intentionally not configured — `aws-amplify/auth` is no
// longer imported anywhere in this app. If something tries to call
// `getCurrentUser()` it will fail, which is the desired migration
// signal: every Cognito callsite should be ported to the new
// session model.
Amplify.configure(
  {
    API: {
      REST: {
        api: {
          endpoint: import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string,
        },
        'api-noauth': {
          endpoint: import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string,
        },
        'healthcom-api': {
          endpoint: import.meta.env.VITE_HEALTHCOM_API as string,
        },
        'healthcom-api-noauth': {
          endpoint: import.meta.env.VITE_HEALTHCOM_API as string,
        },
      },
    },
  },
  {
    API: {
      REST: {
        headers: async ({ apiName }) => {
          // `api-noauth` is intentionally unauthenticated — login routes,
          // public location lookups, request-flow auth bootstrap, the
          // survey + subscription endpoints. Send an empty bearer so the
          // header is always a string and the wrapper treats the call
          // as public (no token).
          if (apiName === 'api-noauth') {
            return { Authorization: '' };
          }

          // Patient session takes precedence (the portal). When that's
          // not present, fall back to a request-scoped session (public
          // PatientRequest flow), then a kiosk-scoped session (paired
          // kiosk device). Each surface only ever carries one of the
          // three at a time, so the precedence here is just "use
          // whatever's available".
          const token =
            portalSession.getToken() || requestSession.getToken() || kioskSession.getToken();

          return { Authorization: token ? `Bearer ${token}` : '' };
        },
        retryStrategy: { strategy: 'no-retry' },
      },
    },
  }
);

// Configure the Safari <=16 fetch fallback with the same endpoints. On
// Safari/iOS <=16, ChiroUpAPI routes requests through Safari16API instead
// of aws-amplify/api, and that layer needs its own endpoint registry.
//
// This app doesn't use Cognito, so override the fallback's Authorization
// resolver to mirror the Amplify REST `headers` callback above: use whatever
// custom session token is currently available (portal, then request, then
// kiosk).
Safari16API.configure(
  {
    api: import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string,
    'api-noauth': import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string,
  },
  {
    resolveAuthHeader: () => {
      const token =
        portalSession.getToken() || requestSession.getToken() || kioskSession.getToken();
      return token ? `Bearer ${token}` : '';
    },
  }
);

const updateScrollbarWidth = () => {
  const w = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  document.documentElement.style.setProperty('--scrollbar-w', `${w}px`);
};

updateScrollbarWidth();
window.addEventListener('resize', updateScrollbarWidth);

// Tag the document when running inside the native Capacitor shell
// (iOS/Android). The WKWebView rubber-band scrolls the whole document
// even when content fits, dragging the sticky header up under the status
// bar. The `capacitor-native` class scopes the app-shell scroll lock in
// index.css to the native app only — desktop/mobile web keep normal
// document scrolling (no rubber-band problem there). Capacitor injects
// `window.Capacitor` into the WebView, so no import is needed.
if (
  (
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.()
) {
  document.documentElement.classList.add('capacitor-native');
}

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

// Reaching this point means the entry module executed, so this boot is not a
// stale-shell failure. Disarm the index.html self-heal guard BEFORE rendering
// (so a throw during render can't leave it armed), release the one-shot retry
// marker so a future stale boot in this tab can heal again, and strip the
// `?cb=` cache-bust the self-heal may have added so URLs stay clean.
(window as unknown as { __hcBooted?: boolean }).__hcBooted = true;
try {
  sessionStorage.removeItem('hc-boot-retry');
} catch {
  /* storage blocked, nothing to clear */
}
if (new URLSearchParams(window.location.search).has('cb')) {
  const u = new URL(window.location.href);
  u.searchParams.delete('cb');
  window.history.replaceState({}, '', u.toString());
}

root.render(<App />);
